import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { SiteLogo } from "@/components/Logo";

const HERO_IMG = "https://images.unsplash.com/photo-1580536793208-117fdb20ffc1?fm=jpg&q=85&w=2400&auto=format&fit=crop";

const LINES = ["EVERY TEAM.", "EVERY SCORE.", "DFW METROPLEX."];

function MaskedLine({ text, delay }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className="block font-display text-5xl uppercase leading-[0.95] tracking-tight text-white sm:text-7xl lg:text-8xl"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {text.includes("SCORE") ? (
          <>{text.split(" ")[0]} <span className="text-volt">{text.split(" ")[1]}</span></>
        ) : text.includes("METROPLEX") ? (
          <>DFW <span className="text-volt">METROPLEX.</span></>
        ) : (
          text
        )}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} className="grain relative flex min-h-[88vh] items-end overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y }}>
        <img src={HERO_IMG} alt="Dallas skyline at night with Reunion Tower" className="h-[120%] w-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/30" />
        <div className="stadium-beam absolute inset-0" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-16 pt-32 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.05, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mb-6"
        >
          <SiteLogo className="h-20 w-20 drop-shadow-[0_0_25px_rgba(56,189,248,0.45)] sm:h-24 sm:w-24" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.6 }}
          className="mb-6 inline-flex items-center gap-2 rounded-full border border-volt/40 bg-ink/60 px-4 py-1.5 backdrop-blur-md"
        >
          <MapPin className="h-3.5 w-3.5 text-volt" />
          <span className="font-score text-xs tracking-widest text-volt">AROUND THE DFW METROPLEX</span>
        </motion.div>

        <h1 className="max-w-5xl" data-testid="hero-headline">
          {LINES.map((l, i) => <MaskedLine key={l} text={l} delay={0.25 + i * 0.15} />)}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7 }}
          className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
        >
          Scores, odds, schedules, news and rosters for all 31 pro, semi-pro and college squads
          across the DFW metroplex — refreshed up to the minute.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.7 }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-scoreboards"
            onClick={() => scrollTo("scoreboards")}
            className="group inline-flex items-center gap-2 rounded-sm bg-volt px-7 py-3 font-display text-xl tracking-widest text-ink transition-transform hover:-translate-y-0.5"
          >
            LIVE SCOREBOARDS
            <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
          </button>
          <button
            data-testid="hero-cta-teams"
            onClick={() => scrollTo("teams")}
            className="rounded-sm border border-slate-600 px-7 py-3 font-display text-xl tracking-widest text-slate-200 transition-colors hover:border-volt hover:text-volt"
          >
            BROWSE 31 TEAMS
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-14 grid max-w-2xl grid-cols-3 divide-x divide-bline border-y border-bline"
          data-testid="hero-stats"
        >
          {[["31", "TEAMS TRACKED"], ["16", "LEAGUES COVERED"], ["365", "DAYS ON THE WIRE"]].map(([n, label]) => (
            <div key={label} className="px-4 py-4 sm:px-6">
              <p className="font-display text-3xl text-volt sm:text-4xl">{n}</p>
              <p className="mt-1 font-score text-[10px] tracking-widest text-slate-500">{label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
